import React, { useState } from "react";
import { Link } from "react-router-dom";
import useForm from "../ExtraPages/useForm";
import Success from "../ExtraPages/Success";
import "./jobspace-auth.css";

/*
  JobSpaceRegister — the "JobSpace Register.dc.html" design (mirrored split-
  screen) reworked to drive the application's REAL registration flow. The form
  fields and submit are powered by the existing `useForm` hook (the same hook
  the legacy FormSignUp page used): it validates, runs the OTP/email step, and
  on success renders the existing `Success` component which finalizes the
  account. Behaviour is identical to the old /signup — only the UI is restyled.
*/

const EMPLOYER_URL = "https://ciam.noser.com.ph/registrationforapp"; //"http://178.128.104.131:9094/registration-form";

const Logo = () => (
  <div style={{ width: 30, height: 30, borderRadius: 9, background: "linear-gradient(145deg,#00b368,#008a4d)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 12px rgba(0,154,87,.32)" }}>
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M9 7V5.5A2.5 2.5 0 0 1 11.5 3h1A2.5 2.5 0 0 1 15 5.5V7m-9 0h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2Z" stroke="#fff" strokeWidth="2" strokeLinecap="round" /></svg>
  </div>
);

const GoogleIcon = () => (
  <svg width="19" height="19" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.5 12.2c0-.7-.1-1.4-.2-2H12v3.8h5.9a5 5 0 0 1-2.2 3.3v2.7h3.6c2.1-2 3.2-4.9 3.2-7.8Z" /><path fill="#34A853" d="M12 23c2.9 0 5.4-1 7.2-2.7l-3.6-2.7c-1 .7-2.3 1.1-3.6 1.1-2.8 0-5.1-1.9-6-4.4H2.3v2.8A11 11 0 0 0 12 23Z" /><path fill="#FBBC05" d="M6 14.3a6.6 6.6 0 0 1 0-4.2V7.3H2.3a11 11 0 0 0 0 9.8L6 14.3Z" /><path fill="#EA4335" d="M12 5.5c1.6 0 3 .5 4.1 1.6l3.1-3.1A11 11 0 0 0 2.3 7.3L6 10.1c.9-2.6 3.2-4.6 6-4.6Z" /></svg>
);

const Check = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="m5 12 4.5 4.5L19 7" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
);

const input = { width: "100%", padding: "12px 16px", border: "1.5px solid #dde5df", borderRadius: 100, fontFamily: "inherit", fontSize: 15, outline: "none", background: "#fff" };
const label = { display: "block", fontSize: 13, fontWeight: 700, marginBottom: 6 };
const errText = { margin: "5px 4px 0", fontSize: 12, color: "#c0493e", fontWeight: 600 };

const PERKS = [
  "One-click Easy Apply to thousands of jobs",
  "Personalized job matches every day",
  "Track your applications in one place",
];

export default function JobSpaceRegister() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const submitForm = () => setIsSubmitted(true);
  // Real registration logic, identical to the legacy FormSignUp page.
  const { handleChange, handleSubmit, values, errors, dissableBtn, handleCheckBox } = useForm(submitForm);
  const [showEmployerModal, setShowEmployerModal] = useState(false);

  // After a valid submit the existing Success component takes over (OTP +
  // account finalization), mirroring the old /signup behaviour.
  if (isSubmitted) return <Success />;

  return (
    <div className="jsl-root" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", minHeight: "100vh", display: "flex", background: "#f4f7f4", color: "#16271f" }}>
      {/* promo panel */}
      <div className="jsl-promo jsl-left" style={{ flex: 1, margin: "16px 0 16px 16px", borderRadius: 28, background: "linear-gradient(155deg,#0a8a4e,#066b3c)", position: "relative", overflow: "hidden", display: "flex", flexDirection: "column", justifyContent: "flex-end", padding: 48, color: "#fff" }}>
        <div style={{ position: "absolute", top: -80, left: -60, width: 280, height: 280, borderRadius: "50%", background: "rgba(255,255,255,.08)" }} />
        <div style={{ position: "absolute", top: 130, left: 150, width: 130, height: 130, borderRadius: "50%", background: "rgba(255,255,255,.06)" }} />
        <div style={{ position: "relative" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "7px 14px", borderRadius: 100, background: "rgba(255,255,255,.15)", fontSize: 13, fontWeight: 600, marginBottom: 28 }}>🚀 Join in under a minute</div>
          <h2 style={{ margin: 0, fontSize: 34, fontWeight: 800, lineHeight: 1.2, letterSpacing: "-.02em", maxWidth: 420 }}>Your next opportunity starts here.</h2>
          <p style={{ margin: "16px 0 34px", fontSize: 16, lineHeight: 1.6, color: "rgba(255,255,255,.85)", maxWidth: 400 }}>Create a free account to save jobs, apply in one click, and get matched with roles built for you.</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 15, maxWidth: 380 }}>
            {PERKS.map((p) => (
              <div key={p} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ flex: "none", width: 30, height: 30, borderRadius: "50%", background: "rgba(255,255,255,.18)", display: "flex", alignItems: "center", justifyContent: "center" }}><Check /></div>
                <span style={{ fontSize: 15, fontWeight: 500, color: "rgba(255,255,255,.92)" }}>{p}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* form column */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "32px 40px" }}>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Link to="/" style={{ display: "flex", alignItems: "center", gap: 9, textDecoration: "none" }}>
            <Logo />
            <span style={{ fontWeight: 800, fontSize: 20, letterSpacing: "-.02em", color: "#16271f" }}>job<span style={{ color: "#009b57" }}>space</span></span>
          </Link>
        </div>

        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px 0" }}>
          <form onSubmit={handleSubmit} className="jsl-stagger" style={{ width: "100%", maxWidth: 420 }}>
            <h1 style={{ margin: 0, fontSize: 30, fontWeight: 800, letterSpacing: "-.025em" }}>Create your account </h1>
            <p style={{ margin: "9px 0 22px", fontSize: 15, color: "#6c7d74", fontWeight: 500 }}>Free forever. No credit card needed.</p>

            <button type="button" className="jsa-google" style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, padding: 12, border: "1.5px solid #dde5df", borderRadius: 100, background: "#fff", fontFamily: "inherit", fontSize: 15, fontWeight: 600, color: "#2c3d34", cursor: "pointer" }}>
              <GoogleIcon />Sign up with Google
            </button>

            <div style={{ display: "flex", alignItems: "center", gap: 14, margin: "18px 0" }}>
              <div style={{ flex: 1, height: 1, background: "#e2e9e3" }} />
              <span style={{ fontSize: 13, color: "#9aa89f", fontWeight: 600 }}>or</span>
              <div style={{ flex: 1, height: 1, background: "#e2e9e3" }} />
            </div>

            {/* employer option — redirects to the dedicated employer registration */}
            <label style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 16, padding: "11px 14px", border: "1.5px solid #e2e9e3", borderRadius: 14, background: "#fbfdfc", cursor: "pointer", fontSize: 13.5, fontWeight: 600, color: "#3c4d44" }}>
              <input type="checkbox" checked={showEmployerModal} onChange={(e) => setShowEmployerModal(e.target.checked)} style={{ width: 16, height: 16, accentColor: "#009b57" }} />
              Are you registering as an Employer?
            </label>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 13 }}>
              <div>
                <label style={label}>First name</label>
                <input name="firstName" value={values.firstName || ""} onChange={handleChange} placeholder="Juan" className="jsa-input" style={input} />
                {errors.firstName && <p style={errText}>{errors.firstName}</p>}
              </div>
              <div>
                <label style={label}>Last name</label>
                <input name="lastName" value={values.lastName || ""} onChange={handleChange} placeholder="dela Cruz" className="jsa-input" style={input} />
                {errors.lastName && <p style={errText}>{errors.lastName}</p>}
              </div>
            </div>

            <div style={{ marginTop: 13 }}>
              <label style={label}>Email address</label>
              <input name="email" value={values.email || ""} onChange={handleChange} placeholder="you@email.com" className="jsa-input" style={input} />
              {errors.email && <p style={errText}>{errors.email}</p>}
            </div>

            <div style={{ marginTop: 13 }}>
              <label style={label}>Mobile number</label>
              <input name="contact" value={values.contact || ""} onChange={handleChange} placeholder="09XXXXXXXXX" maxLength={11} className="jsa-input" style={input} />
              {errors.contact && <p style={errText}>{errors.contact}</p>}
            </div>

            <div style={{ marginTop: 13 }}>
              <label style={label}>Password</label>
              <input name="password" type="password" value={values.password || ""} onChange={handleChange} placeholder="At least 6 characters" className="jsa-input" style={input} />
              {errors.password && <p style={errText}>{errors.password}</p>}
            </div>

            <div style={{ marginTop: 13 }}>
              <label style={label}>Confirm password</label>
              <input name="confirm_password" type="password" value={values.confirm_password || ""} onChange={handleChange} placeholder="Re-enter your password" className="jsa-input" style={input} />
              {errors.confirm_password && <p style={errText}>{errors.confirm_password}</p>}
            </div>

            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, margin: "18px 0 20px", cursor: "pointer" }}>
              <input type="checkbox" onChange={handleCheckBox} style={{ width: 17, height: 17, marginTop: 1, accentColor: "#009b57" }} />
              <span style={{ fontSize: 13, color: "#6c7d74", lineHeight: 1.5, fontWeight: 500 }}>By creating your account you agree to JobSpace's <a style={{ color: "#009b57", fontWeight: 600, textDecoration: "none" }}>Terms</a> and <a style={{ color: "#009b57", fontWeight: 600, textDecoration: "none" }}>Privacy Policy</a>.</span>
            </label>

            <button type="submit" disabled={dissableBtn} className="jsa-primary" style={{ display: "block", textAlign: "center", width: "100%", padding: 14, border: "none", borderRadius: 100, background: "#009b57", color: "#fff", fontFamily: "inherit", fontSize: 15, fontWeight: 700, cursor: dissableBtn ? "not-allowed" : "pointer", opacity: dissableBtn ? 0.55 : 1, boxShadow: "0 6px 16px rgba(0,154,87,.3)" }}>Create account</button>

            <p style={{ margin: "20px 0 0", textAlign: "center", fontSize: 14, color: "#6c7d74", fontWeight: 500 }}>Already have an account? <Link to="/signin" style={{ color: "#009b57", fontWeight: 700, textDecoration: "none" }}>Sign in</Link></p>
          </form>
        </div>
      </div>

      {/* employer redirect confirmation */}
      {showEmployerModal && (
        <div onClick={() => setShowEmployerModal(false)} style={{ position: "fixed", inset: 0, zIndex: 60, background: "rgba(16,32,25,.5)", backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 420, background: "#fff", borderRadius: 20, boxShadow: "0 30px 90px rgba(10,30,20,.4)", padding: 28 }}>
            <h2 style={{ margin: 0, fontSize: 19, fontWeight: 800, letterSpacing: "-.01em" }}>Register as an Employer?</h2>
            <p style={{ margin: "10px 0 22px", fontSize: 14.5, color: "#6c7d74", fontWeight: 500, lineHeight: 1.55 }}>You'll be redirected to the JobSpace Employer Registration to set up your company account.</p>
            <div style={{ display: "flex", gap: 11 }}>
              <a href={EMPLOYER_URL} className="jsa-primary" style={{ flex: 1, textAlign: "center", padding: 12, borderRadius: 12, background: "#009b57", color: "#fff", fontFamily: "inherit", fontSize: 14.5, fontWeight: 700, textDecoration: "none" }}>Proceed</a>
              <button type="button" onClick={() => setShowEmployerModal(false)} className="jsa-google" style={{ flex: "none", padding: "12px 18px", border: "1.5px solid #dde5df", borderRadius: 12, background: "#fff", color: "#2c3d34", fontFamily: "inherit", fontSize: 14.5, fontWeight: 700, cursor: "pointer" }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
